import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, Users, AlertTriangle } from "lucide-react";

function getEventTab(status) {
  if (status === "pending") return "pending";
  if (status === "declined") return "declined";
  if (status === "completed") return "archive";
  return "upcoming";
}

export function DayDetailPanel({ selectedDay, events, colorMap, COLORS, onClose }) {
  const navigate = useNavigate();
  if (!selectedDay) return null;

  return (
    <Card className="border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {new Date(selectedDay + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 text-xs">Close</Button>
        </div>
        <div className="space-y-2">
          {events.map(ev => (
            <div
              key={ev.event_id}
              className={`p-3 rounded-sm border cursor-pointer ${COLORS[colorMap[ev.event_id]]} hover:opacity-80`}
              onClick={() => navigate('/events', { state: { highlightEvent: ev.event_id, eventTab: getEventTab(ev.status) } })}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{ev.name}</span>
                <Badge variant="outline" className="text-xs">{ev.status}</Badge>
              </div>
              <div className="text-xs opacity-70 mt-1">
                {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` — ${ev.end_date}` : ''}
                {ev.venue ? ` · ${ev.venue}` : ''}
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs opacity-60">
                <span className="flex items-center gap-1"><Package className="w-3 h-3" />{ev.gear_assignments?.length || 0} gear</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ev.crew_assignments?.length || 0} crew</span>
                {ev.revenue > 0 && <span className="text-emerald-400">${ev.revenue?.toLocaleString()}</span>}
              </div>
            </div>
          ))}
          {events.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">No events on this day</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ConflictDetailsCard({ conflicts, getCrewName }) {
  if (conflicts.length === 0) return null;

  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Conflict Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {conflicts.map((c) => (
          <div key={`${c.event1.event_id}-${c.event2.event_id}`} className="p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-red-300">{c.event1.name}</span>
              <span className="text-muted-foreground">&amp;</span>
              <span className="font-semibold text-red-300">{c.event2.name}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Overlap: {c.event1.start_date} — {c.event1.end_date} / {c.event2.start_date} — {c.event2.end_date}
            </div>
            {c.gear_details?.length > 0 && (
              <div className="mt-1.5">
                {c.gear_details.map((g) => (
                  <div key={g.name} className="text-xs text-red-300/80">
                    <span className="font-medium">{g.name}</span>: need {g.needed}, have {g.total} (short {g.shortage})
                  </div>
                ))}
              </div>
            )}
            {c.shared_crew?.length > 0 && (
              <div className="text-xs text-red-300/80 mt-1">
                Crew: {c.shared_crew.map(id => getCrewName(id)).join(', ')}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
